import { z } from "zod";
/*
 * An organisation's canvas theme: the fonts, corners and colours its admins set on top of the MLX
 * app's design system. It is applied when a canvas is shown, as the theme tokens the canvas's
 * classes read, so every canvas the organisation has takes it at once; a brand set inside one
 * canvas (a prospect's colours) still wins over it.
 */
export const CANVAS_THEME_FONTS = ["inter", "system", "serif", "mono"];
export const canvasThemeFontSchema = z.enum(CANVAS_THEME_FONTS);
/** Each font choice as the CSS stack a canvas uses; Inter is embedded in every canvas, the rest are the reader's own. */
export const CANVAS_THEME_FONT_STACKS = {
    inter: `"Inter Variable", ui-sans-serif, system-ui, sans-serif`,
    system: `ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`,
    serif: `"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, "Times New Roman", serif`,
    mono: `ui-monospace, "SF Mono", Menlo, Consolas, monospace`,
};
/** A font a theme sets: one of the app's choices, or "custom", the organisation's own (customFont). */
export const canvasThemeFontChoiceSchema = z.union([canvasThemeFontSchema, z.literal("custom")]);
/** The family an organisation's own font is declared under in a canvas. */
export const CANVAS_THEME_CUSTOM_FONT_FAMILY = "MLX Organisation Font";
export const CANVAS_THEME_RADII = ["none", "small", "default", "large"];
export const canvasThemeRadiusSchema = z.enum(CANVAS_THEME_RADII);
/** The app's --radius for each choice; "default" keeps the app's own. */
export const CANVAS_THEME_RADIUS_VALUES = {
    none: "0rem",
    small: "0.25rem",
    large: "0.875rem",
};
/** The largest font file a theme may embed in every canvas it styles. */
export const CANVAS_THEME_MAX_FONT_BYTES = 600 * 1024;
/** Font files a theme may embed, by media type. */
export const CANVAS_THEME_FONT_TYPES = ["font/woff2", "font/woff", "font/ttf", "font/otf"];
export const canvasThemeFontTypeSchema = z.enum(CANVAS_THEME_FONT_TYPES);
/** The largest logo a theme may carry into every canvas it signs. */
export const CANVAS_THEME_MAX_LOGO_BYTES = 150 * 1024;
/** Logo files a theme may carry, by media type: shown as an image, so an SVG's script never runs. */
export const CANVAS_THEME_LOGO_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
export const canvasThemeLogoTypeSchema = z.enum(CANVAS_THEME_LOGO_TYPES);
const canvasThemeLogoSchema = z.object({
    contentType: canvasThemeLogoTypeSchema,
    base64: z.string().min(4).max(Math.ceil(CANVAS_THEME_MAX_LOGO_BYTES / 3) * 4),
}).strict();
const colorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/u, "Use a colour as #RRGGBB.");
const fontNameSchema = z.string().trim().min(1).max(80).regex(/^[\p{L}\p{N} .&'-]+$/u, "Use letters, numbers and spaces.");
const canvasThemeColorsSchema = z.object({
    accent: colorSchema.nullable().default(null),
    background: colorSchema.nullable().default(null),
    surface: colorSchema.nullable().default(null),
    text: colorSchema.nullable().default(null),
    border: colorSchema.nullable().default(null),
}).strict();
const canvasThemeFields = {
    /** The app's light or dark theme, which every other choice is set on top of. */
    mode: z.enum(["light", "dark"]).default("light"),
    /** The body text's font; headings use it too unless headingFont is set. */
    bodyFont: canvasThemeFontChoiceSchema.default("inter"),
    headingFont: canvasThemeFontChoiceSchema.nullable().default(null),
    radius: canvasThemeRadiusSchema.default("default"),
    /** Colours replacing the app's; any left null keep the app's own. Charts take the accent as their first series. */
    colors: canvasThemeColorsSchema.default({ accent: null, background: null, surface: null, text: null, border: null }),
    /** The organisation's logo, which leads each canvas's byline and sign-off in place of its letter. */
    logo: canvasThemeLogoSchema.nullable().default(null),
};
const canvasThemeCustomFontSchema = z.object({
    /** What the organisation calls it; the canvas declares it under CANVAS_THEME_CUSTOM_FONT_FAMILY. */
    name: fontNameSchema,
    contentType: canvasThemeFontTypeSchema,
    bytes: z.number().int().positive().max(CANVAS_THEME_MAX_FONT_BYTES),
}).strict();
export const canvasThemeSchema = z.object({
    ...canvasThemeFields,
    /** The organisation's own font, uploaded with the theme, which bodyFont and headingFont may choose ("custom"). */
    customFont: canvasThemeCustomFontSchema.nullable().default(null),
}).strict();
/**
 * PUT /api/client/organization/canvas-theme. A custom font carries its file (base64) when it is new;
 * without one the stored file is kept, under the name given.
 */
export const canvasThemeUpdateSchema = z.object({
    ...canvasThemeFields,
    customFont: z.object({
        name: fontNameSchema,
        file: z.object({
            contentType: canvasThemeFontTypeSchema,
            base64: z.string().min(1).max(Math.ceil(CANVAS_THEME_MAX_FONT_BYTES / 3) * 4),
        }).strict().optional(),
    }).strict().nullable().default(null),
}).strict().refine((update) => update.customFont !== null || (update.bodyFont !== "custom" && update.headingFont !== "custom"), { message: "Upload the font to use your own.", path: ["customFont"] });
/** The app's own design, unchanged. */
export const DEFAULT_CANVAS_THEME = canvasThemeSchema.parse({});
export const canvasThemeResponseSchema = z.object({
    theme: canvasThemeSchema,
    canEdit: z.boolean(),
    updatedAt: z.number().nullable(),
    /** The custom font as a data URL, when asked for (?includeFont=1), for a preview. */
    customFontUrl: z.string().optional(),
}).strict();
/** Text must read against its ground at least this well (WCAG AA for body text). */
export const CANVAS_THEME_MIN_CONTRAST = 4.5;
/** The accent must stand out from the background at least this well (WCAG AA for large text and controls). */
export const CANVAS_THEME_MIN_ACCENT_CONTRAST = 3;
/**
 * The app's ground, ink and chart series in each mode (its light blue and Charcoal Blue themes), which
 * the theme's own colours are measured and mixed against; a test holds them to the generated design system.
 */
export const CANVAS_THEME_MODE_TOKENS = {
    light: {
        background: "0 0% 98%",
        foreground: "0 0% 8%",
        chart: ["217 91% 45%", "173 68% 28%", "17 88% 38%", "38 85% 31%", "0 65% 42%"],
    },
    dark: {
        background: "0 0% 12.5%",
        foreground: "0 0% 94%",
        chart: ["217 78% 67%", "158 60% 55%", "27 78% 58%", "42 85% 65%", "4 66% 70%"],
    },
};
/** Series hues closer than this read as the same series. */
const CHART_MIN_HUE_GAP = 30;
// Colour arithmetic the theme needs: the app's tokens hold "H S% L%" triples.
function channels(hex) {
    return [1, 3, 5].map((at) => Number.parseInt(hex.slice(at, at + 2), 16) / 255);
}
/** A #RRGGBB colour as the "H S% L%" triple the app's theme tokens hold. */
export function hexToHslTriple(hex) {
    const [r, g, b] = channels(hex);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const lightness = (max + min) / 2;
    const delta = max - min;
    const round = (value) => Math.round(value * 10) / 10;
    if (delta === 0)
        return `0 0% ${round(lightness * 100)}%`;
    const saturation = delta / (1 - Math.abs(2 * lightness - 1));
    const hue = max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
    return `${round((hue * 60 + 360) % 360)} ${round(saturation * 100)}% ${round(lightness * 100)}%`;
}
/** An "H S% L%" triple as #RRGGBB. */
export function hslTripleToHex(triple) {
    const [h, s, l] = triple.replaceAll("%", "").trim().split(/\s+/u).map(Number);
    const saturation = s / 100;
    const lightness = l / 100;
    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const channel = (n) => {
        const k = (n + h / 30) % 12;
        return lightness - chroma / 2 * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    };
    return `#${[0, 8, 4].map((n) => Math.round(channel(n) * 255).toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}
function luminance(hex) {
    const [r, g, b] = channels(hex).map((value) => value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG contrast between two #RRGGBB colours, from 1 to 21. */
export function contrastRatio(first, second) {
    const [light, dark] = [luminance(first), luminance(second)].sort((a, b) => b - a);
    return (light + 0.05) / (dark + 0.05);
}
/** A #RRGGBB colour `amount` (0 to 1) of the way from `from` to `to`. */
export function mixHex(from, to, amount) {
    const [a, b] = [channels(from), channels(to)];
    return `#${a.map((value, index) => Math.round((value + (b[index] - value) * amount) * 255)
        .toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}
/** Text that reads on a colour: white, or near-black where white would not. */
export function readableOn(hex) {
    return contrastRatio(hex, "#FFFFFF") >= CANVAS_THEME_MIN_CONTRAST ? "#FFFFFF" : "#141414";
}
/** The ground and ink a theme ends up with: its own, or its mode's. */
export function canvasThemeGround(theme) {
    const mode = CANVAS_THEME_MODE_TOKENS[theme.mode];
    return {
        background: theme.colors.background ?? hslTripleToHex(mode.background),
        text: theme.colors.text ?? hslTripleToHex(mode.foreground),
    };
}
/** Whether the theme sets the organisation's own font for any text. */
export function canvasThemeUsesCustomFont(theme) {
    return theme.customFont !== null && (theme.bodyFont === "custom" || theme.headingFont === "custom");
}
/** Whether the theme's ground is dark, so the canvas's own controls and scrollbars should be. */
export function canvasThemeIsDark(theme) {
    return luminance(canvasThemeGround(theme).background) < 0.2;
}
/** Why a theme's colours would be hard to read, in words, or null when they are fine. */
export function canvasThemeContrastProblem(theme) {
    const { background, text } = canvasThemeGround(theme);
    const reads = (ground, ink, minimum, what) => {
        const ratio = contrastRatio(ground, ink);
        // Rounded down, so a ratio just short of the minimum never reads as meeting it (2.97 shows 2.9, not 3.0).
        return ratio < minimum ? `${what} reads at ${(Math.floor(ratio * 10) / 10).toFixed(1)}:1; it needs at least ${minimum}:1.` : null;
    };
    return reads(background, text, CANVAS_THEME_MIN_CONTRAST, "Text on the background")
        ?? (theme.colors.surface ? reads(theme.colors.surface, text, CANVAS_THEME_MIN_CONTRAST, "Text on the surface") : null)
        ?? (theme.colors.accent
            ? reads(background, theme.colors.accent, CANVAS_THEME_MIN_ACCENT_CONTRAST, "The accent on the background")
            : null);
}
/** The ink between `text` and `ground` that still reads on `ground`, as far toward it as `amount` allows. */
function quietInk(text, ground, amount) {
    for (let step = amount; step > 0; step -= 0.02) {
        const ink = mixHex(text, ground, step);
        if (contrastRatio(ink, ground) >= CANVAS_THEME_MIN_CONTRAST)
            return ink;
    }
    return text;
}
/**
 * The app tokens a theme replaces, as CSS custom properties over the mode's own (the app's light
 * :root, or its .dark theme), each a custom property. Neutral steps (muted, secondary, borders, quiet ink) are mixed from the
 * theme's ground and ink so they stay in key with them; fonts are the stacks canvases read through
 * --canvas-font-sans and --canvas-font-heading.
 */
export function canvasThemeVariables(theme) {
    const variables = {};
    const set = (names, hex) => {
        for (const name of names)
            variables[name] = hexToHslTriple(hex);
    };
    const { colors } = theme;
    if (colors.background || colors.text) {
        const { background, text } = canvasThemeGround(theme);
        const toward = (amount) => mixHex(background, text, amount);
        set(["--background"], background);
        set(["--foreground", "--card-foreground", "--popover-foreground", "--secondary-foreground", "--accent-foreground",
            "--badge-outline-foreground", "--badge-secondary-foreground", "--content-code-foreground"], text);
        set(["--card"], toward(0.025));
        set(["--popover", "--surface", "--surface-raised"], background);
        set(["--secondary", "--badge-secondary-bg", "--content-code-bg"], toward(0.035));
        set(["--accent", "--badge-outline-bg"], toward(0.045));
        set(["--muted", "--control"], toward(0.06));
        set(["--border"], toward(0.1));
        set(["--content-code-border"], toward(0.12));
        set(["--input"], toward(0.14));
        set(["--badge-outline-border"], toward(0.18));
        set(["--control-border"], toward(0.55));
        set(["--muted-foreground"], quietInk(text, background, 0.4));
        set(["--tertiary-foreground", "--placeholder"], quietInk(text, background, 0.46));
    }
    if (colors.surface)
        set(["--card", "--popover", "--surface", "--surface-raised"], colors.surface);
    if (colors.border)
        set(["--border", "--input"], colors.border);
    if (colors.accent) {
        const deeper = mixHex(colors.accent, theme.mode === "dark" ? "#FFFFFF" : "#000000", 0.14);
        set(["--primary", "--link", "--solid", "--ring"], colors.accent);
        set(["--primary-dark", "--link-hover", "--solid-hover"], deeper);
        set(["--primary-foreground", "--solid-foreground"], readableOn(colors.accent));
        variables["--chart-1"] = colors.accent;
        // As in the app's own themes: the accent takes the first series, and a series whose hue it would
        // repeat takes the colour the accent displaced.
        const chart = CANVAS_THEME_MODE_TOKENS[theme.mode].chart;
        const [accentHue, accentSaturation] = hexToHslTriple(colors.accent).split(" ").map((part) => Number.parseFloat(part));
        const gap = (triple) => {
            const distance = Math.abs(Number.parseFloat(triple) - accentHue) % 360;
            return Math.min(distance, 360 - distance);
        };
        const clash = chart.slice(1).map((triple, index) => ({ index: index + 1, gap: gap(triple) }))
            .filter((series) => series.gap < CHART_MIN_HUE_GAP).sort((a, b) => a.gap - b.gap)[0];
        if (accentSaturation >= 15 && clash)
            variables[`--chart-${clash.index + 1}`] = `hsl(${chart[0]})`;
    }
    if (theme.radius !== "default")
        variables["--radius"] = CANVAS_THEME_RADIUS_VALUES[theme.radius];
    // The organisation's font falls back to Inter (or the body's font, for headings) while it loads, or if it cannot.
    const own = (stack) => `"${CANVAS_THEME_CUSTOM_FONT_FAMILY}", ${stack}`;
    const body = theme.bodyFont === "custom" ? own(CANVAS_THEME_FONT_STACKS.inter) : CANVAS_THEME_FONT_STACKS[theme.bodyFont];
    if (theme.bodyFont !== "inter")
        variables["--canvas-font-sans"] = body;
    if (theme.headingFont) {
        variables["--canvas-font-heading"] = theme.headingFont === "custom" ? own(body) : CANVAS_THEME_FONT_STACKS[theme.headingFont];
    }
    return variables;
}
